import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  EmployeerSpokenTypes,
  EnquiryFormFieldMapping,
  EnquiryStatuses,
  IEnquiryDetail,
  IRecommenedLinkInfo,
  PermissionToContactTypes,
  YesNoTypes,
  EmploymentTypes,
} from '../../../models/Enquiry'
import Conditional from '../../common/Conditional'
import EnquiryDetailRow from './helpers/EnquiryDetailRow'
import '../Enquiries.css'
import { Button } from 'semantic-ui-react'
import { getEnquiryDetailLabel } from '../EnquiryCommon'

export interface IEnquiryDetailProps {
  data: IEnquiryDetail
  handleErrors?: (any) => void
}

const EnquiryDetail = (props: IEnquiryDetailProps) => {
  const mountedRef = useRef(false)
  const [data, setData] = useState<IEnquiryDetail>()
  const history = useHistory()

  useEffect(() => {
    mountedRef.current = true
    if (mountedRef.current) {
      setData(props.data)
    }
    return () => {
      mountedRef.current = false
    }
  }, [props.data])

  const resumeEnquiry = (id: string) => {
    history.push('/my-account/enquiry/' + id + '/edit/1')
  }

  const respondViewRFI = (id: string) => {
    history.push('/my-account/enquiry/' + id + '/requestInformation?id=' + id)
    return
  }

  const cancelEnquiry = () => {
    history.push(
      '/my-account/enquiry/cancel?id=' +
        data?.id +
        '&url=' +
        window.location.href
    )
  }

  const backtoEnquiries = () => {
    history.push('/my-account/enquiries')
  }

  const address = []
  if (data?.businessABN != null) address.push(data?.businessABN)
  if (data?.businessName != null) address.push(data?.businessName)
  if (data?.businessAddressLine1 != null)
    address.push(data?.businessAddressLine1)
  if (data?.businessAddressLine2 != null)
    address.push(data?.businessAddressLine2)
  if (data?.businessSuburb != null) address.push(data?.businessSuburb)
  if (data?.businessState != null && data?.businessPostcode != null)
    address.push(data?.businessState + ' ' + data?.businessPostcode)
  if (data?.nomineePhone != null) address.push(data?.nomineePhone)

  return (
    <Conditional if={data}>
      <div className="horizontal-menu thirds">
        {data?.isSaved ? (
          <Button
            className="button is-primary"
            onClick={() => resumeEnquiry(data?.id)}
          >
            Resume enquiry
          </Button>
        ) : null}

        {data?.hasRFI ? (
          <>
            <Conditional if={data?.hasRFIPendingResponse}>
              <Button
                className="button is-primary"
                onClick={() => respondViewRFI(data?.id)}
              >
                Respond to request for information
              </Button>
            </Conditional>
            <Conditional if={!data?.hasRFIPendingResponse}>
              <Button
                className="button is-primary"
                onClick={() => respondViewRFI(data?.id)}
              >
                View responses
              </Button>
            </Conditional>
          </>
        ) : null}

        {data?.isOpen ||
        data?.isSaved ||
        data?.status === EnquiryStatuses.InProgress ||
        data?.status === EnquiryStatuses.EnquiryRecommendedLinksProvided ||
        data?.status ===
          EnquiryStatuses.EnquiryResponseToRequestForInformationReceived ||
        data?.status === EnquiryStatuses.EnquiryRequestForInformationSent ? (
          <Button className="button is-secondary" onClick={cancelEnquiry}>
            Cancel enquiry
          </Button>
        ) : null}
        <Button
          className={`${
            data?.status === EnquiryStatuses.EnquiryCancel
              ? 'is-primary'
              : 'is-secondary'
          } button`}
          onClick={() => backtoEnquiries()}
        >
          Return to enquiry list
        </Button>
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <EnquiryDetailRow title="Status" value={data?.status} />
            <EnquiryDetailRow
              title="Your current personal situation"
              value={data?.customerType}
            />
            <tr>
              <td>
                <strong>You need information about</strong>
              </td>
              <td>
                {data?.requestForInfoType}
                <ol>
                  {data?.requestForInfoSubTypes?.map((st, index) => (
                    <li key={index}>{st}</li>
                  ))}
                </ol>
              </td>
            </tr>

            <EnquiryDetailRow
              title="Your other workplace issues or questions"
              value={data?.otherQuestions}
            />
            <tr>
              <td>
                <strong>Your business details</strong>
              </td>
              <td>
                <ol>
                  {address.map((ad, key) => (
                    <li className="no-bullets" key={key}>
                      {ad}
                    </li>
                  ))}
                </ol>
              </td>
            </tr>

            <Conditional if={data?.isEE || data?.isenle}>
              <EnquiryDetailRow
                title="Your suburb, state and postcode"
                value={`${data?.employeeSuburb} \n\n ${data?.employeeState}, ${data?.employeePostcode}`}
              />
            </Conditional>

            <EnquiryDetailRow
              title={EnquiryFormFieldMapping.get('noOfEmployees')}
              value={data?.noOfEmployees}
            />
            <EnquiryDetailRow
              title={EnquiryFormFieldMapping.get('employmentTypeId')}
              value={data?.employmentType}
            />
            <Conditional
              if={
                data?.employmentType === EmploymentTypes.Unsure &&
                (data?.isEE || data?.isenle)
              }
            >
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('paidLeaveId')}
                value={data?.paidLeave}
              />
            </Conditional>
            <EnquiryDetailRow
              title={getEnquiryDetailLabel('apprenticeOrTraineeId', data)}
              value={data?.apprenticeOrTrainee}
            />

            <Conditional if={data?.isEE || data?.isenle}>
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('independentContractorId')}
                value={data?.independentContractor}
              />
              <Conditional if={data?.independentContractor === YesNoTypes.Yes}>
                <EnquiryDetailRow
                  title={EnquiryFormFieldMapping.get(
                    'genuineContractEmployeeTypeId'
                  )}
                  value={data?.genuineContractEmployeeType}
                />
              </Conditional>
            </Conditional>

            <Conditional if={data?.completingInternship !== null}>
              <EnquiryDetailRow
                title={getEnquiryDetailLabel('completingInternship', data)}
                value={data?.completingInternship === true ? 'Yes' : 'No'}
              />
            </Conditional>

            <EnquiryDetailRow
              title={EnquiryFormFieldMapping.get('personDescriptionIds')}
              values={data?.personDescription}
            />

            <Conditional if={data?.visaHolder !== null}>
              <EnquiryDetailRow
                title={getEnquiryDetailLabel('visaHolder', data)}
                value={data?.visaHolder === true ? 'Yes' : 'No'}
              />
              <Conditional if={data?.visaHolder === true}>
                <EnquiryDetailRow
                  title={EnquiryFormFieldMapping.get('visaTypeId')}
                  value={data?.visaType}
                />
                {data?.isEmployeer}
                <Conditional if={data?.isenle || data?.isEE}>
                  <EnquiryDetailRow
                    title={EnquiryFormFieldMapping.get('countryId')}
                    value={data?.country}
                  />
                </Conditional>
              </Conditional>
            </Conditional>

            <Conditional if={data?.isEE}>
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('dateOfBirth')}
                value={data?.dateOfBirth}
              />
            </Conditional>

            <EnquiryDetailRow
              title={EnquiryFormFieldMapping.get('officeLocationId')}
              value={data?.officeLocation}
            />

            <EnquiryDetailRow
              title={EnquiryFormFieldMapping.get('interpreterRequired')}
              value={
                data?.interpreterRequired === true
                  ? 'Yes'
                  : data?.interpreterRequired === false
                  ? 'No'
                  : 'Not answered'
              }
            />
            <Conditional if={data?.language !== null}>
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('languageId')}
                value={data?.language}
              />
            </Conditional>

            <Conditional if={data?.isEE || data?.isenle}>
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('occupation')}
                value={data?.occupation}
              />
            </Conditional>

            <EnquiryDetailRow
              title={EnquiryFormFieldMapping.get('mainDuties')}
              value={data?.mainDuties}
            />

            <Conditional if={data?.isEE || data?.isenle}>
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('haveSpokenToEmployer')}
                value={data?.haveSpokenToEmployer}
              />
              <Conditional
                if={data?.haveSpokenToEmployer === EmployeerSpokenTypes.NotYet}
              >
                <EnquiryDetailRow
                  title={EnquiryFormFieldMapping.get(
                    'notSpokenToEmployerReasonId'
                  )}
                  value={data?.notSpokenToEmployerReason}
                />
              </Conditional>
              <Conditional
                if={data?.haveSpokenToEmployer === EmployeerSpokenTypes.Yes}
              >
                <EnquiryDetailRow
                  title={EnquiryFormFieldMapping.get(
                    'outcomeOfSpokenToEmployer'
                  )}
                  value={data?.outcomeOfSpokenToEmployer}
                />
              </Conditional>
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('areOthersAffectedId')}
                value={data?.areOthersAffected}
              />
            </Conditional>

            <EnquiryDetailRow
              title={EnquiryFormFieldMapping.get('employmentStartDate')}
              value={data?.employmentStartDate}
            />

            <Conditional
              if={
                data?.isSBE ||
                data?.isEmployeer ||
                data?.isER ||
                data?.isERR ||
                data?.isIC ||
                data?.isenle ||
                data?.isEE
              }
            >
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('employmentEndDate')}
                value={data?.employmentEndDate}
              />
            </Conditional>

            <Conditional if={data?.isEE || data?.isenle}>
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('hourlyPayRate')}
                value={data?.hourlyPayRate}
              />
            </Conditional>

            <Conditional
              if={(data?.isEE || data?.isenle) && data?.hasSupportingEvidence}
            >
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('hasSupportingEvidence')}
                value={data?.hasSupportingEvidence === true ? 'Yes' : 'No'}
              />
              <Conditional if={data?.hasSupportingEvidence === true}>
                <EnquiryDetailRow
                  title={EnquiryFormFieldMapping.get('supportingEvidenceIds')}
                  values={data?.supportingEvidence}
                />
              </Conditional>
            </Conditional>

            <EnquiryDetailRow
              title={EnquiryFormFieldMapping.get('otherInformation')}
              value={data?.otherInformation}
            />

            <Conditional
              if={
                data?.nominateSomeoneToAct !== null &&
                (data?.isEE || data?.isenle)
              }
            >
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('nominateSomeoneToAct')}
                value={data?.nominateSomeoneToAct === true ? 'Yes' : 'No'}
              />
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('nomineeFullName')}
                value={data?.nomineeFullName}
              />
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('nomineePhone')}
                value={data?.nomineePhone}
              />
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('nomineeEmailAddress')}
                value={data?.nomineeEmailAddress}
              />
            </Conditional>

            <Conditional
              if={
                data?.permissionToContact !== null &&
                (data?.isEE || data?.isenle)
              }
            >
              <EnquiryDetailRow
                title={EnquiryFormFieldMapping.get('permissionToContact')}
                value={data?.permissionToContact}
              />
              <Conditional
                if={data?.permissionToContact === PermissionToContactTypes.Yes}
              >
                <EnquiryDetailRow
                  title={EnquiryFormFieldMapping.get('employeerFullName')}
                  value={data?.employeerFullName}
                />
                <EnquiryDetailRow
                  title={EnquiryFormFieldMapping.get('employeerPhone')}
                  value={data?.employeerPhone}
                />
                <EnquiryDetailRow
                  title={EnquiryFormFieldMapping.get('employeerEmailAddress')}
                  value={data?.employeerEmailAddress}
                />
              </Conditional>
            </Conditional>
            <tr>
              <td>
                <strong>Recommended links</strong>
              </td>
              <td>
                <Conditional if={data?.recommendedLinks?.length > 0}>
                  <ul>
                    {data?.recommendedLinks?.map(
                      (link: IRecommenedLinkInfo, index) => {
                        return (
                          <li key={index}>
                            <a target="_blank" href={link.url} rel="noreferrer">
                              {link.name}
                            </a>
                          </li>
                        )
                      }
                    )}
                  </ul>
                </Conditional>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Response</strong>
              </td>
              <td>{data?.enquiryResponse}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="horizontal-menu thirds">
        {data?.isSaved ? (
          <Button
            className="button is-primary"
            onClick={() => resumeEnquiry(data?.id)}
          >
            Resume enquiry
          </Button>
        ) : null}
        {data?.isOpen ||
        data?.isSaved ||
        data?.status === EnquiryStatuses.InProgress ||
        data?.status === EnquiryStatuses.EnquiryRecommendedLinksProvided ||
        data?.status ===
          EnquiryStatuses.EnquiryResponseToRequestForInformationReceived ||
        data?.status === EnquiryStatuses.EnquiryRequestForInformationSent ? (
          <Button className="button is-secondary" onClick={cancelEnquiry}>
            Cancel enquiry
          </Button>
        ) : null}
        <Button
          className={`${
            data?.status === EnquiryStatuses.EnquiryCancel
              ? 'is-primary'
              : 'is-secondary'
          } button`}
          onClick={() => backtoEnquiries()}
        >
          Return to enquiry list
        </Button>
      </div>
    </Conditional>
  )
}
export default EnquiryDetail
